/**
 * Cirrus Master Form block.
 *
 * Visitors pick an option and the matching Gravity Form is fetched from the
 * REST API and mounted in place. Only the selected form is ever in the DOM, so
 * forms offered here never end up duplicated on the page by this block.
 */
(function () {
	'use strict';

	const ROOT_SELECTOR = '[data-master-form]';
	const TIMEOUT_MS = 15000;

	class LoadError extends Error {
		constructor(message, { status = 0, code = '' } = {}) {
			super(message);
			this.status = status;
			this.code = code;
		}
	}

	// Scripts in markup inserted with innerHTML don't run, so each one is
	// replaced by a fresh copy, in order, waiting for external ones to load.
	async function runScripts(container) {
		const scripts = Array.from(container.querySelectorAll('script'));

		for (const original of scripts) {
			const script = document.createElement('script');
			Array.from(original.attributes).forEach(function (attr) {
				script.setAttribute(attr.name, attr.value);
			});

			if (original.src) {
				await new Promise(function (resolve, reject) {
					script.onload = resolve;
					script.onerror = function () {
						reject(new LoadError(`Failed to load script ${original.src}`));
					};
					original.replaceWith(script);
				});
			} else {
				script.textContent = original.textContent;
				original.replaceWith(script);
			}
		}
	}

	function campaignId() {
		return new URLSearchParams(window.location.search).get('campaign_id') || '';
	}

	async function fetchForm(endpoint, formId, sig, signal) {
		const url = new URL(endpoint, window.location.href);
		url.searchParams.set('form_id', formId);
		url.searchParams.set('sig', sig);

		const campaign = campaignId();
		if (campaign) {
			url.searchParams.set('campaign_id', campaign);
		}

		const response = await fetch(url.toString(), {
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
			signal,
		});

		let data = null;
		try {
			data = await response.json();
		} catch (e) {
			throw new LoadError('Invalid JSON response', { status: response.status });
		}

		if (!response.ok) {
			throw new LoadError(data?.message || `HTTP ${response.status}`, {
				status: response.status,
				code: data?.code || '',
			});
		}

		if (!data || typeof data.html !== 'string' || !data.html.trim()) {
			throw new LoadError('Empty form markup', { status: response.status });
		}

		return data.html;
	}

	function init(root) {
		const endpoint = root.dataset.endpoint;
		const selector = root.querySelector('[data-master-form-selector]');
		const select = root.querySelector('[data-master-form-select]');
		const next = root.querySelector('[data-master-form-next]');
		const change = root.querySelector('[data-master-form-change]');
		const target = root.querySelector('[data-master-form-target]');
		const error = root.querySelector('[data-master-form-error]');
		const errorMessage = root.querySelector('[data-master-form-error-message]');
		const retry = root.querySelector('[data-master-form-retry]');

		// Leave the editor preview static.
		if (!endpoint || !select || !next || !target || root.hasAttribute('data-master-form-preview')) return;

		let controller = null;
		let mountedFormId = null;

		function setLoading(isLoading) {
			root.classList.toggle('is-loading', isLoading);
			next.setAttribute('aria-busy', isLoading ? 'true' : 'false');
		}

		function hideError() {
			error.hidden = true;
			retry.hidden = true;
			errorMessage.textContent = '';
		}

		function showError(err) {
			const unavailable = err.status === 404 || err.code === 'form_unavailable';
			errorMessage.textContent = unavailable
				? root.dataset.unavailableMessage
				: root.dataset.errorMessage;
			retry.hidden = unavailable;
			error.hidden = false;
		}

		function unmount() {
			target.innerHTML = '';
			mountedFormId = null;
			change.hidden = true;
			selector.hidden = false;
		}

		async function load() {
			const option = select.options[select.selectedIndex];
			const formId = select.value;
			if (!formId || !option) return;

			// A newer request replaces any request still in flight.
			if (controller) controller.abort();
			const current = new AbortController();
			controller = current;
			const timeout = setTimeout(function () {
				current.abort();
			}, TIMEOUT_MS);

			hideError();
			setLoading(true);

			try {
				const html = await fetchForm(endpoint, formId, option.dataset.sig, current.signal);
				if (controller !== current) return;

				const existing = document.getElementById(`gform_${formId}`);
				if (existing && !root.contains(existing)) {
					console.error(
						`[Master Form] Form ${formId} is already rendered elsewhere on this page. ` +
							'Gravity Forms scripts may target the wrong copy.'
					);
				}

				unmount();
				target.innerHTML = html;

				// GF builds the form action from the request URI, which is the REST endpoint here.
				// Submissions (including AJAX page changes) must post back to this page instead.
				target.querySelectorAll('form[id^="gform_"]').forEach(function (form) {
					const url = new URL(window.location.href);
					url.hash = `gf_${form.dataset.formid || formId}`;
					form.setAttribute('action', url.pathname + url.search + url.hash);
				});

				await runScripts(target);
				if (controller !== current) return;

				mountedFormId = formId;
				selector.hidden = true;
				change.hidden = false;

				// Skip hidden inputs and GF's honeypot, which is visually hidden but not type="hidden".
				const field = Array.from(
					target.querySelectorAll(
						'.gfield input:not([type="hidden"]):not([disabled]), .gfield select:not([disabled]), .gfield textarea:not([disabled])'
					)
				).find(function (el) {
					return !el.closest('.gform_validation_container') && el.offsetParent !== null;
				});
				if (field) field.focus();
			} catch (err) {
				// Aborted because a newer request replaced this one.
				if (controller !== current) return;

				console.error('[Master Form] Failed to load form', formId, err);
				unmount();
				showError(err);
			} finally {
				clearTimeout(timeout);
				if (controller === current) {
					controller = null;
					setLoading(false);
				}
			}
		}

		select.addEventListener('change', function () {
			next.disabled = !select.value;
			hideError();
		});

		next.addEventListener('click', load);
		retry.addEventListener('click', load);

		change.addEventListener('click', function () {
			unmount();
			hideError();
			select.focus();
		});

		// Once submitted, the confirmation replaces the form and the selection is final.
		if (window.jQuery) {
			jQuery(document).on('gform_confirmation_loaded', function (event, formId) {
				if (String(formId) === String(mountedFormId)) {
					change.hidden = true;
				}
			});
		}

		next.disabled = !select.value;
	}

	document.addEventListener('DOMContentLoaded', function () {
		document.querySelectorAll(ROOT_SELECTOR).forEach(init);
	});
})();
